import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { IKeyValuePair } from "@app/interfaces/IKeyValuePair";
import { CommserverMigrationStatus, IConfigurableCameraDevice, IPeripheral, IViewCommissionPeripheral } from "@app/interfaces/IPeripheral";
import { IPlot } from "@app/interfaces/IPlot";
import { IVehiclePeripheralMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { CameraOnlineStatus } from "@app/main/components/videos/models/camera-online-status";
import { IVehicleOnlineStatus } from "@app/main/components/videos/models/IVehicleOnlineStatus";
import { CameraOnlineHistoryService } from "@app/main/components/videos/services/camera-online-history.service";
import { IPeripheralMetadata } from "@app/main/modules/foundation/_routes/hardware/_models/IPeripheralMetadata";
import { IPeripheralMetadataAutoUpdateDto } from "@app/main/modules/foundation/_routes/hardware/_models/IPeripheralMetadataAutoUpdateDto";
import { IPulseRecordPayload } from "@app/main/modules/foundation/_routes/hardware/_models/IPulseRecordPayload";
import { AccountFeatureService } from "@app/services/accountFeature.service";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { EnumService } from "@app/services/enum.service";
import { ProductsService } from "@app/services/products.service";
import { combineLatest, from, Observable } from "rxjs";
import { map, mergeMap, switchMap } from "rxjs/operators";
import { IPulseRecord } from "../_routes/hardware/_models/IPulseRecord";
import { HardwareMappingService } from "../_routes/hardware/_services/hardware-mapping.service";
import { FoundationVehiclesService } from "./vehicle.service";

@Injectable({
    providedIn: 'root'
})
export class PeripheralService {
    private peripheralsCache: string = 'foundationPeripheralsCache';

    constructor(private readonly http: HttpClient,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly dataCacheService: DataCacheService,
        private readonly cameraOnlineHistoryService: CameraOnlineHistoryService,
        private readonly hardwareMappingService: HardwareMappingService,
        private readonly productsService: ProductsService,
        private readonly vehicleService: FoundationVehiclesService) { }

    getPeripherals(invalidateCache = false): Observable<IPeripheral[]> {
        return this.dataCacheService.getOrRequest(this.peripheralsCache, () => this.getPeripheralsFromDb(), { invalidateCache });
    }

    getPeripheralsFromDb(): Observable<IPeripheral[]> {
        return this.http.get<IPeripheral[]>(`/api/foundation/peripherals/`);
    }

    getPeripheralsForVehicle(vehicleId: number): Observable<IPeripheral[]> {
        return this.http.get<IPeripheral[]>(`/api/foundation/peripherals/${vehicleId}`);
    }

    getPeripheralMappings(): Observable<IVehiclePeripheralMapping[]> {
        return this.http.get<IVehiclePeripheralMapping[]>(`/api/foundation/vehicles/peripheralmappings`);
    }

    getAllPeripheralsWithMappings(): Observable<IPeripheral[]> {
        return combineLatest([
            this.getPeripherals(),
            this.getPeripheralMappings(),
            this.vehicleService.getVehicles()
        ]).pipe(
            map(([peripherals, mappings, vehicles]) => {
                const vehiclesMap = new Map(vehicles.map(vehicle => [vehicle.Id, vehicle]));
                mappings.forEach(mapping => mapping.vehicle = vehiclesMap.get(mapping.VehicleId));
                return [peripherals, mappings];
            }),
            mergeMap(([peripherals, mappings]: [IPeripheral[], IVehiclePeripheralMapping[]]) => {
                const peripheralMappingsMap = new Map(mappings.map(mapping => [mapping.Serial, mapping]));
                const peripheralsWithPeripheralMappings = this.hardwareMappingService.applyVehicleMappingsToUnits(peripherals, peripheralMappingsMap);

                return this.hardwareMappingService.getProductsAndMap(peripheralsWithPeripheralMappings);
            }));
    }

    getAllConfigurableCameras(): Observable<IConfigurableCameraDevice[]> {
        return combineLatest([
            this.getAllPeripheralsWithMappings(),
            from(this.cameraOnlineHistoryService.getCameraOnlineStatuses())
        ]).pipe(
            map(([cameras, onlineStatuses]) => {
                const onlineStatusesMap = new Map(onlineStatuses.map(status => [status.VehicleId, status]));

                const configurableCameras: IConfigurableCameraDevice[] = this.filterCameras(cameras, onlineStatusesMap);

                return configurableCameras;
            }),
            switchMap((cameras) => {
                return this.getPulseRecords(cameras);
            })
        )

    }

    getOnlineStatusForVehicle(camera: IConfigurableCameraDevice): IVehicleOnlineStatus {
        if (!camera.onlineStatus) {
            return {
                lastOnlineTime: null,
                online: false,
                hasStreaming: false,
                sortingTime: null
            }
        }

        const lastOnlineTime = new Date(camera.onlineStatus.LastOnlineTime);

        const hasStreaming = camera.Product?.ProductMetadata?.CameraFeatures?.LiveStream;

        return {
            lastOnlineTime,
            online: camera.onlineStatus.Online,
            hasStreaming,
            sortingTime: lastOnlineTime
        }
    }


    getPulseRecords(cameras: IConfigurableCameraDevice[]): Observable<IConfigurableCameraDevice[]> {
        return this.http.get<IPulseRecord[]>('/api/cameras/commserver/pulserecords/latest').pipe(
            map(records => {
                const recordsMap = new Map(records.map(record => [record.Serial, record]));

                cameras.forEach(camera => {
                        
                    if (!recordsMap.has(camera.Serial)) {
                        camera.migrationStatus = CommserverMigrationStatus.Required
                        return;
                    }
                    const latestRecord = recordsMap.get(camera.Serial);
                    const parsedPayload = JSON.parse(latestRecord.Payload) as IPulseRecordPayload;
                                        
                    camera.currentFW = parsedPayload.HardwareFirmwareVersion;

                    if (parsedPayload.HardwareConfigName.indexOf(';') > -1) {
                        parsedPayload.HardwareConfigName = parsedPayload.HardwareConfigName.substring(0, parsedPayload?.HardwareConfigName.indexOf(';'))
                    }

                    camera.migrationStatus = CommserverMigrationStatus.Completed;
                    camera.currentConfig = parsedPayload.HardwareConfigName;
                    camera.currentTelematicsAppVersion = !!parsedPayload.Applications.length ? parsedPayload.Applications[0].AppVersion : "";
                    camera.currentUpdaterAppVersion = parsedPayload.FirmwareVersion;
                    camera.cameraFootageStart = parsedPayload.ContinuousFootageStart;
                    
                });

                return cameras;
            })
        )
    }

    getPeripheralsWithMappingsForVehicle(vehicleId?: number): Observable<IPeripheral[]> {
        const request = !!vehicleId ? this.getPeripheralsForVehicle(vehicleId) : this.getPeripherals();

        return request
            .pipe(switchMap(peripherals => {
                return this.productsService.getProductUiViewModels().pipe(map(products => {
                    peripherals.forEach(peripheral => {
                        peripheral.Product = products.find(product => product.ProductId === peripheral.ProductId)
                    })

                    return peripherals;
                }))
            }))
    }

    getPeripheralBySerial(serial: string): Observable<IPeripheral> {
        return this.getAllPeripheralsWithMappings().pipe(map(peripherals => {
            const peripheral = peripherals.find(peripheral => peripheral.Serial == serial);
            return peripheral;
        }))
    }



    createInstalledPeripheralList(peripherals: IPeripheral[]): { installed: IPeripheral[], notInstalled: IPeripheral[] } {
        const featuresEnumArray: IKeyValuePair<typeof AccountFeatures>[] = EnumService.enumToArray("AccountFeatures", AccountFeatures);

        const peripheralsCurrentlyInstalled: IPeripheral[] = [];
        const peripheralsNotInstalled: IPeripheral[] = [];

        featuresEnumArray.forEach(featureEnum => {
            const feature: AccountFeatures = +featureEnum.value;
            const peripheral = this.findInstalledPeripheralForFeature(feature, peripherals, peripheralsCurrentlyInstalled);

            if (peripheral) {
                this.hardwareMappingService.setMetaDataOnUnit(peripheral, feature);
                peripheralsCurrentlyInstalled.push(peripheral);
            }
            else {
                peripheralsNotInstalled.push(peripheral);
            }
        });

        return {
            installed: peripheralsCurrentlyInstalled,
            notInstalled: peripheralsNotInstalled
        };
    }

    getInstalledPeripheralsForVehicle(vehicleId: number): Observable<IViewCommissionPeripheral[]> {
        return this.getPeripheralsWithMappingsForVehicle(vehicleId).pipe(map(peripherals => {
            const result = this.createInstalledPeripheralList(peripherals);
            return result.installed as IViewCommissionPeripheral[];
        }));
    }

    getExtendedPlotsByImei(imei: string, jobItemVehicleId?: number): Observable<IPlot[]> {
        return this.http.get<IPlot[]>(`/api/foundation/units/${imei}/extendedPlots`, { params: { jobItemVehicleId } })
    }

    getPeripheralMetadata(serial: string): Observable<IPeripheralMetadata> {
        return this.http.get<IPeripheralMetadata>(`/api/foundation/peripherals/${serial}/metadata`);
    }

    setAutoUpdateProperty(updateDto: IPeripheralMetadataAutoUpdateDto) {
        return this.http.post(`/api/foundation/peripherals/autoupdate`, updateDto);
    }

    private findInstalledPeripheralForFeature(featureIndex: number, peripherals, peripheralsCurrentlyInstalled): IPeripheral {
        if (!peripherals?.length) {
            return null;
        }

        let chosenPeripheral: IPeripheral;

        peripherals.forEach(peripheral => {
            const peripheralFeatures = this.accountFeatureService.findFeaturesFromFeaturesEnum(peripheral.Product.Features);

            if (!peripheralFeatures?.length || !!chosenPeripheral) {
                return;
            }

            for (let i = 0; i < peripheralFeatures.length; i++) {
                const feature = peripheralFeatures[i];
                const alreadyAddedPeripheral = peripheralsCurrentlyInstalled.find(installedPeripheral => installedPeripheral.Serial === peripheral.Serial);

                if (feature === featureIndex && !alreadyAddedPeripheral) {
                    chosenPeripheral = peripheral;

                    break;
                }
            }
        });

        return chosenPeripheral;
    }

    private filterCameras(peripherals: IPeripheral[], onlineStatusesMap: Map<number, CameraOnlineStatus>): IConfigurableCameraDevice[] {
        return (peripherals as IConfigurableCameraDevice[]).filter(camera => {
            if (camera.Product?.GroupId === ProductVendor.RTL) {
                camera.onlineStatus = this.getOnlineStatusForCamera(camera, onlineStatusesMap);
                return camera;
            }
        });
    }

    private getOnlineStatusForCamera(camera: IConfigurableCameraDevice, onlineStatuses: Map<number, CameraOnlineStatus>): CameraOnlineStatus {
        return onlineStatuses.get(camera.vehicleMapping?.VehicleId);
    }

}
